import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Link,
  Tooltip,
  TableSortLabel
} from '@mui/material';
import { Edit, Delete, Restore } from '@mui/icons-material';
import { getFieldLabel, formatCellValue } from '../utils/masterDataUtils';

const MasterDataTable = ({
  data,
  loading,
  effectiveType,
  currentType,
  manufacturerSubTab,
  canEdit,
  canDelete,
  onEdit,
  onRestore,
  onDelete,
  onShowConfigurationDetails,
  sortField,
  sortDirection,
  onSort
}) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {effectiveType.fields.map((field) => (
              <TableCell key={field} sortDirection={sortField === field ? sortDirection : false}>
                <TableSortLabel
                  active={sortField === field}
                  direction={sortField === field ? sortDirection : 'asc'}
                  onClick={() => onSort && onSort(field)}
                  sx={{ 
                    '& .MuiTableSortLabel-icon': {
                      opacity: sortField === field ? 1 : 0.3
                    }
                  }}
                >
                  {getFieldLabel(field, currentType, manufacturerSubTab)}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Status</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={effectiveType.fields.length + 2} align="center">
                Loading...
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={effectiveType.fields.length + 2} align="center">
                No data found
              </TableCell>
            </TableRow>
          ) : (
            data.map((item, index) => {
              const isNewManufacturerGroup = currentType.key === 'manufacturers' && 
                index > 0 && data[index - 1].name !== item.name;
              const isFirstRowOfGroup = currentType.key === 'manufacturers' && 
                (index === 0 || data[index - 1].name !== item.name);
              
              return (
                <TableRow 
                  key={item._id}
                  sx={isNewManufacturerGroup ? {
                    '& td': {
                      borderTop: '3px solid #757575 !important',
                      paddingTop: '16px'
                    }
                  } : isFirstRowOfGroup && currentType.key === 'manufacturers' ? {
                    '& td': {
                      backgroundColor: '#f5f5f5'
                    }
                  } : {}}
                >
                  {effectiveType.fields.map((field) => {
                    const cellValue = formatCellValue(item, field, currentType, manufacturerSubTab);
                    const isAssemblyTypeField = field === 'assemblyType' && currentType.key === 'assembly-type-configurations';
                    const isOperatorTemplateField = field === 'template' && currentType.key === 'operators' && item.template;
                    
                    return (
                      <TableCell key={field}>
                        {isAssemblyTypeField && onShowConfigurationDetails ? (
                          <Tooltip title="Click to view configuration details">
                            <Link
                              component="button"
                              variant="body2"
                              onClick={(e) => {
                                e.preventDefault();
                                onShowConfigurationDetails(item);
                              }}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                color: 'primary.main',
                                '&:hover': {
                                  color: 'primary.dark',
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              {cellValue}
                            </Link>
                          </Tooltip>
                        ) : isOperatorTemplateField ? (
                          <Tooltip title="Download template letterpad">
                            <Link
                              href={item.template}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={item.templateFileName || `${item.name || 'operator'}-template`}
                              sx={{
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                color: 'primary.main',
                                '&:hover': {
                                  color: 'primary.dark',
                                  textDecoration: 'underline'
                                }
                              }}
                            >
                              Download
                            </Link>
                          </Tooltip>
                        ) : (
                          cellValue
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell>
                    <Chip
                      label={item.isActive ? 'Active' : 'Archived'}
                      color={item.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {canEdit && item.isActive && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(item)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && item.isActive && (
                      <Tooltip title="Archive">
                        <IconButton size="small" color="error" onClick={() => onDelete(item)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && !item.isActive && onRestore && (
                      <Tooltip title="Restore">
                        <IconButton size="small" color="success" onClick={() => onRestore(item)}>
                          <Restore />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && !item.isActive && (
                      <Tooltip title="Permanently Delete">
                        <IconButton size="small" color="error" onClick={() => onDelete(item)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MasterDataTable;
